import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { faPen } from '@fortawesome/pro-light-svg-icons/faPen';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { PlugBar } from '../components/PlugBar';
import { useCampaignPool } from '../hooks/useCampaignPool';
import { useReviewQueue } from '../hooks/useReviewQueue';
import { outreachCampaignTabPath, outreachReviewPath } from '../outreachPaths';
import type { CampaignDetail, OutreachCampaignLive } from '../outreachTypes';
import { SendingStoppedState } from './SendingStoppedState';
import { TodayCard } from './TodayCard';
import { StatsRow } from './StatsRow';
import { FoundLastNight } from './FoundLastNight';
import { MailboxRamp } from './MailboxRamp';
import { NeverContactSide } from './NeverContactSide';
import { EditorialHelperCard } from './EditorialHelperCard';
import { campaignPageMessages as messages } from './campaignPageMessages';
import { startedToday } from './campaignFormat';
import { UpNextCard } from './upNext/UpNextCard';
import { NextPickCard } from './upNext/NextPickCard';
import { nextPickCount } from './upNext/upNextCopy';
import { useNow } from './upNext/useNow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import styles from './Campaign.module.scss';
/* eslint-enable css-modules/no-unused-class */

const OPEN_STATUSES: ReadonlySet<string> = new Set(['draft', 'needsEdit']);

interface TodayTabProps {
  accountId: number;
  live: OutreachCampaignLive;
  campaign: CampaignDetail | undefined;
}

/** The clock of the day words and the titles; the countdowns tick by themselves every second. */
const CLOCK_MS = 60 * 1000;

/**
 * The Today tab (design outreach-v2 g1 top and side, s2-s4b): the ring, "Up next" (the people that
 * Plug picked, with names, before the send) or the next pick at 06:00, the pitches that wait for
 * the artist, the five totals (hidden until the first pitch went out); on the side "Found last
 * night", the mailbox, "Never contact" and the Spotify editorial helper.
 */
const TodayTabComponent: React.FC<TodayTabProps> = ({ accountId, live, campaign }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pool } = useCampaignPool(accountId, live.campaignUuid);
  const { queue } = useReviewQueue(accountId, live.campaignUuid);
  const waiting = useMemo(
    () => (queue?.messages ?? []).filter((message) => OPEN_STATUSES.has(message.status)).length,
    [queue]
  );
  const rate = campaign?.dailyRate ?? null;
  // On the day of the start no night with a find passed: "so far", every fit (QA B06).
  const soFar = campaign !== undefined && startedToday(campaign.startedAt);
  const fits = soFar ? pool?.total : pool?.newTotal;
  const now = useNow(CLOCK_MS);
  const { planned } = live;
  const seeAll = useCallback(
    // eslint-disable-next-line no-void
    () => void navigate(outreachCampaignTabPath(live.campaignUuid, 'people')),
    [live.campaignUuid, navigate]
  );

  return (
    <div className={styles.grid}>
      <div className={styles.col}>
        {live.mailboxNeedsReconnect && (
          <SendingStoppedState mailbox={live.mailboxAddress} waiting={live.waitingCount} />
        )}
        <TodayCard live={live} rate={rate} newFits={fits ?? null} soFar={soFar} now={now} />
        {planned !== null && planned.total > 0 && (
          <UpNextCard planned={planned} warmup={live.warmup} rate={rate} now={now} onSeeAll={seeAll} />
        )}
        {live.phase === 'sending' && planned !== null && planned.total === 0 && planned.nextBatchAt !== null && (
          <NextPickCard
            accountId={accountId}
            campaignUuid={live.campaignUuid}
            releaseDay={campaign?.releaseDate?.slice(0, 10) ?? null}
            nextBatchAt={planned.nextBatchAt}
            count={nextPickCount(rate, live.warmup, planned.nextBatchAt)}
            onSeeAll={seeAll}
          />
        )}
        {waiting > 0 && (
          <PlugBar
            title={intl.formatMessage(messages.reviewWaiting, { count: waiting })}
            sub={intl.formatMessage(messages.reviewWaitingSub)}
            testId="today-review-waiting"
          >
            <SMLButton
              color="primary"
              size="sm"
              omitCap
              leftIcon={faPen}
              // eslint-disable-next-line no-void
              onClick={() => void navigate(outreachReviewPath(live.campaignUuid))}
              testID="today-open-review"
            >
              {intl.formatMessage(messages.reviewOpen)}
            </SMLButton>
          </PlugBar>
        )}
        {live.totals.pitched.total > 0 && <StatsRow totals={live.totals} />}
      </div>
      <div className={styles.col}>
        <FoundLastNight
          pool={pool}
          rate={rate}
          endsAt={campaign?.endsAt ?? null}
          kinds={campaign?.kinds ?? []}
          soFar={soFar}
        />
        <MailboxRamp warmup={live.warmup} rate={rate} />
        <NeverContactSide accountId={accountId} />
        {campaign !== undefined && <EditorialHelperCard accountId={accountId} campaign={campaign} />}
      </div>
    </div>
  );
};

export const TodayTab = TypedMemo(TodayTabComponent);
